<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'

import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {toast} from '@/components/ui/toast'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type {OrgMember} from '@/types'

const props = defineProps<{ orgId: number }>()

const orgStore = useOrgStore()

const userStore = useUserStore()

const accessStore = useAccessStore()
const me = computed(() => userStore.member?.member_id)
const candidates = computed<OrgMember[]>(() =>
  (orgStore.currentMembers as OrgMember[]).filter(m => m.member_id !== me.value)
)
const selectedId = ref<number | null>(null)
const selected = computed(() => candidates.value.find(m => m.member_id === selectedId.value))
const confirmDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const inProgress = ref(false)

onMounted(() => {
  if (!candidates.value.length) orgStore.fetchOrgMembers(props.orgId)
})

const transfer = async () => {
  if (!selected.value) return
  inProgress.value = true
  try {
    await accessStore.transferOwner(selected.value.member_id)
    await orgStore.fetchOrgMembers(props.orgId)
    toast({title: `${selected.value.display_name} is now the Owner`})
    selectedId.value = null
  } catch {
    toast({title: "Couldn't transfer ownership. Reload and try again.", variant: 'destructive'})
  } finally {
    inProgress.value = false
  }
}
</script>

<template>
  <div class="mt-10 w-1/2 space-y-3" data-testid="owner-transfer">
    <div>
      <h3 class="text-base font-bold">Ownership</h3>
      <p class="text-sm text-muted-foreground">
        You are the Owner of this organization. The Owner always has full access and can't be removed.
        Transfer ownership to another member to step down.
      </p>
    </div>
    <div class="flex items-center gap-2">
      <Select :model-value="selectedId ?? undefined" @update:model-value="v => selectedId = Number(v)">
        <SelectTrigger class="flex-1">
          <SelectValue placeholder="Choose a member" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="m in candidates" :key="m.member_id" :value="m.member_id">
            {{ m.display_name }} · {{ m.email }}
          </SelectItem>
        </SelectContent>
      </Select>
      <Button variant="destructive" :disabled="!selected || inProgress" @click="confirmDialog?.open()">
        Transfer ownership
      </Button>
    </div>
    <DialogConfirm
      ref="confirmDialog"
      title="Transfer ownership?"
      :description="`${selected?.display_name ?? 'This member'} becomes the Owner. You keep your current role, but you won't be able to undo this yourself.`"
      confirm-text="Transfer"
      variant="error"
      @confirm="transfer"
    />
  </div>
</template>
